<template>
  <div id="order-lookup" class="small-margin-top-s medium-margin-top-m">
    <p class="small gray-1-color">
      {{ ORDER_LOOKUP.requiredField }}
    </p>
    <Form id="order-lookup-form" v-slot="{ errors, setErrors }" ref="form" role="main" class="grid-x" @submit="getOrders">
      <div class="cell medium-5">
        <!-- Order Number -->
        <div id="order-number">
          <div class="input-container">
            <Field
              id="order-number-field"
              v-model="formData.orderNumber"
              name="orderNumber"
              placeholder=" "
              type="text"
              maxlength="20"
              :class="{ 'is-invalid-input': !!errors?.orderNumber }"
              :rules="orderNumberRule"
              :validate-on-blur="false"
              :validate-on-change="false"
              :validate-on-input="false"
            />
            <label class="label-top" for="order-number-field">{{ ORDER_LOOKUP.orderNumberLabel }}*</label>
          </div>
          <ErrorMessage id="order-number-error" class="form-error is-visible" name="orderNumber" />
        </div>
        <div id="gift-checkbox" :class="[!!errors?.orderNumber ? 'margin-top-xs' : 'margin-top-s']">
          <CheckboxBasic
            v-model="isGift"
            name="isGift"
            :options="[
              {
                label: ORDER_LOOKUP.receivingGiftText,
                value: true,
              },
            ]"
          />
        </div>
      </div>
      <!-- Email/Phone -->
      <div class="grid-x small-12">
        <template v-if="!isGift[0]">
          <!-- Email Address -->
          <div id="email" class="cell medium-5">
            <div class="input-container">
              <Field
                id="email-field"
                v-model="formData.email"
                name="email"
                placeholder=" "
                type="email"
                :class="{ 'is-invalid-input': !!errors?.email }"
                class="cell small-12"
                :rules="emailRule"
                :disabled="!!formData.phone"
                :validate-on-blur="false"
                :validate-on-change="false"
                :validate-on-input="false"
                :readonly="isEmailReadonly"
                @input="submitted && resetValidationState()"
                @focus="isEmailReadonly = false"
                @blur="isEmailReadonly = true"
              />
              <label class="label-top" for="email-field">{{ ORDER_LOOKUP.emailAddressLabel }}</label>
            </div>
            <ErrorMessage id="email-error" class="form-error is-visible" name="email" />
          </div>
          <p class="small-padding-top-xxs medium-padding-m gray-1-color">
            {{ ORDER_LOOKUP.orLabel }}
          </p>
          <!-- Phone Number -->
          <div id="phone" class="cell medium-5">
            <div class="input-container">
              <Field
                id="phone-field"
                v-model="formData.phone"
                name="phone"
                placeholder=" "
                type="tel"
                :class="{ 'is-invalid-input': !!errors?.phone }"
                class="cell small-12"
                :rules="phoneRule"
                :disabled="!!formData.email"
                maxlength="12"
                :validate-on-blur="false"
                :validate-on-change="false"
                :validate-on-input="false"
                :readonly="isPhoneReadonly"
                @keypress="numbersOnly($event)"
                @input="(formatPhone(), submitted && resetValidationState())"
                @focus="isPhoneReadonly = false"
                @blur="isPhoneReadonly = true"
              />
              <label class="label-top" for="phone-field">{{ ORDER_LOOKUP.phoneNumberLabel }}</label>
            </div>
            <ErrorMessage id="phone-error" class="form-error is-visible" name="phone" />
          </div>
        </template>
        <!-- Zip code -->
        <div v-else id="zip-code" class="cell medium-5">
          <div class="input-container">
            <Field
              id="zip-code-field"
              v-model="formData.zipCode"
              name="zipCode"
              placeholder=" "
              type="text"
              :class="{ 'is-invalid-input': !!errors?.zipCode }"
              class="cell small-12"
              :rules="zipCodeRule"
              maxlength="5"
              :validate-on-blur="false"
              :validate-on-change="false"
              :validate-on-input="false"
              @keypress="numbersOnly($event)"
              @input="submitted && resetValidationState()"
            />
            <label class="label-top" for="zip-code-field">{{ ORDER_LOOKUP.shippingZipcodeLabel }}</label>
          </div>
          <ErrorMessage id="zip-code-error" class="form-error is-visible" name="zipCode" />
        </div>
      </div>
      <PrivacyPractices class="small-12 margin-top-xs" />
      <div class="cell medium-5">
        <button
          id="order-lookup-submit"
          :disabled="!lookupReady || isLoading"
          class="primary button small-margin-top-xs expanded medium-margin-top-s"
          type="submit"
          @click="prepareSubmit()"
        >
          {{ ORDER_LOOKUP.viewOrderText }}
        </button>
        <button ref="resetFormValidation" type="button" hidden @click.prevent="setErrors([])"></button>
      </div>
    </Form>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue';
import { useOrderDetailsStore } from '@/stores/useOrderDetailsStore';
import { UserStatus } from '@vue-util/cookie';
import { veevalidate } from '@vue-util/forms';
import { phoneFormat } from '@vue-util/forms/mask-util';
import { fireTag } from '@vue-util/tagManager';
import CheckboxBasic from '@atomic-ui/checkboxBasic/CheckboxBasic';
import { useRouter } from 'vue-router';
import PrivacyPractices from '@/components/common/PrivacyPractices';
import { ORDER_LOOKUP, PPE_ERROR_TAG } from 'config';

const { Field, Form, ErrorMessage } = veevalidate;
const orderDetailsStore = useOrderDetailsStore();
const router = useRouter();

const isGift = ref([]);
const formData = reactive({
  orderNumber: '',
  phone: '',
  email: '',
  zipCode: '',
});
const payloadObj = ref({});
const submitted = ref(false);
const emailRule = ref('');
const phoneRule = ref('');
const zipCodeRule = ref('');

// Control the readonly state reactively
const isEmailReadonly = ref(true);
const isPhoneReadonly = ref(true);

const isLoading = computed(() => orderDetailsStore.isLoading);
const lookupReady = computed(() => {
  if (!formData.orderNumber?.trim()) return false;
  if (isGift.value[0] && formData.zipCode.length < 3) return false;
  if (!isGift.value[0] && !formData.email && !formData.phone) return false;
  return true;
});
const updateOrderDetailsApiError = computed(() => orderDetailsStore.updateOrderDetailsApiError);

const formatPhone = () => {
  formData.phone = phoneFormat(formData.phone);
};
const numbersOnly = (e) => {
  const char = String.fromCharCode(e.keyCode);
  if (/^[0-9]+$/.test(char)) return true;
  return e.preventDefault();
};
const orderNumberRule = (orderNumber) => {
  const trimmed = typeof orderNumber === 'string' ? orderNumber.trim() : orderNumber;
  if (!trimmed) return ORDER_LOOKUP.noOrderNumberMessage;
  if (trimmed.length > 20) return ORDER_LOOKUP.maximumCharactersMessage;
  if (!/^[A-Za-z0-9]*$/.test(trimmed)) return ORDER_LOOKUP.invalidOrderNumberMessage;
  // Update the form data with trimmed value
  formData.orderNumber = trimmed;
  return true;
};
const prepareSubmit = () => {
  if (typeof formData.orderNumber === 'string') {
    formData.orderNumber = formData.orderNumber.trim();
  }
  submitted.value = true;
  if (formData.email) emailRule.value = 'email';
  if (formData.phone) phoneRule.value = 'phone';
  if (formData.zipCode) zipCodeRule.value = 'zipCode';
};
const resetValidationState = () => {
  if (ref.resetFormValidation) {
    ref.resetFormValidation.click();
  }
  submitted.value = false;
  emailRule.value = '';
  phoneRule.value = '';
  zipCodeRule.value = '';
};
const updatePayload = () => {
  payloadObj.value = {
    orderNumber: formData.orderNumber,
    fromLookup: true,
    isSignedIn: UserStatus.isSignedIn(),
    payload: {},
  };
  if (isGift.value[0]) {
    payloadObj.value.payload.isGiftOption = true;
    payloadObj.value.payload.zipCode = formData.zipCode;
  } else {
    payloadObj.value.payload.email = formData.email;
    payloadObj.value.payload.phoneNumber = formData.phone;
  }
};
const getOrders = async () => {
  if (typeof formData.orderNumber === 'string') {
    formData.orderNumber = formData.orderNumber.trim();
  }
  fireTag('link', {
    ...ORDER_LOOKUP.orderLookupTag,
    gift_order: isGift.value[0] ? 'yes' : 'no',
  });
  updatePayload();
  await orderDetailsStore.GET_ONLINE_ORDER_DETAILS({
    payload: payloadObj.value,
    router,
  });
  if (updateOrderDetailsApiError.value) {
    fireTag('link', {
      ...PPE_ERROR_TAG.error,
      error_code: [updateOrderDetailsApiError.value?.errorCode || PPE_ERROR_TAG.defaultErrorCode],
      error_message: [updateOrderDetailsApiError.value?.message],
    });
  }
};
</script>

<style lang="scss" scoped>
@import '~settingSCSS';
</style>
